import { useState } from 'react';
import { Alert, Button, Drawer, Flex, Loader, Stack, Text, TextInput } from '@mantine/core';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useWorkOsOrganizationLookup } from '@dealstack/frontend/app/pages/Organizations/hooks/useWorkOsOrganizationLookup';
import { useImportOrganization } from '@dealstack/frontend/app/pages/Organizations/hooks/useImportOrganization';
import { normaliseLookupDomain } from '@dealstack/frontend/app/pages/Organizations/components/ImportOrganizationDrawer/ImportOrganizationDrawer.utils';

interface Props {
	opened: boolean;
	onClose: () => void;
}

export const ImportOrganizationDrawer = ({ opened, onClose }: Props) => {
	const tOrganization = useTranslationOrganization();
	const [domainInput, setDomainInput] = useState('');
	const [submittedDomain, setSubmittedDomain] = useState('');

	const { data: lookup, isFetching } = useWorkOsOrganizationLookup(submittedDomain, opened);
	const { mutate: importOrg, isPending } = useImportOrganization();

	const handleClose = () => {
		setDomainInput('');
		setSubmittedDomain('');
		onClose();
	};

	return (
		<Drawer
			opened={opened}
			onClose={handleClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tOrganization('organizations_management.import.drawer.title')}
		>
			<Stack gap="md">
				<Text size="B6Medium" c="dark.0">
					{tOrganization('organizations_management.import.drawer.description')}
				</Text>
				<form
					onSubmit={(event) => {
						event.preventDefault();
						setSubmittedDomain(normaliseLookupDomain(domainInput));
					}}
				>
					<Flex gap="0.5rem" align="flex-end">
						<TextInput
							flex={1}
							size="md"
							data-testid="import-organization-domain"
							value={domainInput}
							onChange={(event) => setDomainInput(event.currentTarget.value)}
							label={tOrganization(
								'organizations_management.import.drawer.fields.domain.label'
							)}
							placeholder={tOrganization(
								'organizations_management.import.drawer.fields.domain.placeholder'
							)}
						/>
						<Button type="submit" disabled={normaliseLookupDomain(domainInput) === ''}>
							{tOrganization('organizations_management.import.drawer.search')}
						</Button>
					</Flex>
				</form>

				{isFetching && <Loader size="sm" />}

				{lookup?.truncated && (
					<Alert color="orange.6" variant="light">
						{tOrganization('organizations_management.import.drawer.truncated', {
							cap: lookup.cap,
						})}
					</Alert>
				)}

				{!isFetching && lookup?.organizations.length === 0 && (
					<Text size="B6Medium" c="dark.0">
						{tOrganization('organizations_management.import.drawer.empty')}
					</Text>
				)}

				{lookup?.organizations.map((organization) => (
					<Flex
						key={organization.authenticationProviderOrgId}
						gap="0.5rem"
						align="center"
						justify="space-between"
					>
						<Stack gap="0">
							<Text size="B6Medium">{organization.name}</Text>
							<Text size="B6Light" c="dark.0">
								{organization.emailDomains.join(', ')}
							</Text>
						</Stack>
						{organization.alreadyImported ? (
							<Text size="B6Light" c="teal.8">
								{tOrganization(
									'organizations_management.import.drawer.already_imported'
								)}
							</Text>
						) : (
							<Button
								variant="outline"
								disabled={isPending}
								onClick={() =>
									importOrg(organization.authenticationProviderOrgId, {
										onSuccess: handleClose,
									})
								}
							>
								{tOrganization('organizations_management.import.drawer.import')}
							</Button>
						)}
					</Flex>
				))}
			</Stack>
		</Drawer>
	);
};
